import * as React from 'react';
import * as R from 'ramda';
import { useDispatch } from 'react-redux';
import { SessionStorageKeys } from './sessionStorageKeys';
import { Config } from '../featureFlags/config';
import { FeatureFlags } from '../featureFlags/featureFlags';
import { updateStateAction } from '../core/redux/updateStateAction';
import { useMount } from '../core/hooks/useMount';
import { useSessionStorage } from '../core/hooks/useSessionStorage';
import { AuthState } from '../auth/authState';
import { LocaleState, defaultLocaleState } from '../localization/localeState';
import { Map } from '../language/models';
import { EmailBody } from '../project/survey/raters/models';
import { useAppState } from '../core/redux/hooks';
import { CollaboratorAuthInfo } from '../project/devPlan/collaboration/currentCollaboratorState';


/**
 * AppStateSessionStorageBridge: a component that is non-rendering and connects Redux AppState to the browser's
 * Session Storage API. This component will load session storage state into Redux onMount, and will store Redux Store
 * changes to Session Storage as they happen.
 *
 * PureComponent: true
 * Redux Connected: true
 */
const AppStateSessionStorageBridge: React.FC = () => {
  // You could make this less code by mashing everything together into a single object, but that would also mean
  // a lot of redundant setting/rendering for any change
  // Third return from `useSessionStorage` is `remove: () => void` for clearing a key

  // Session Storage Hooks ---------------------------------------------------------------------------------------------
  const [authValue, setAuth]                 = useSessionStorage<AuthState>(SessionStorageKeys.Auth);
  const [collabValue, setCollab]             = useSessionStorage<CollaboratorAuthInfo>(SessionStorageKeys.Collaborator);
  const [configValue, setConfig]             = useSessionStorage<Config>(SessionStorageKeys.Config);
  const [featureFlagsValue, setFeatureFlags] = useSessionStorage<FeatureFlags>(SessionStorageKeys.FeatureFlags);
  const [localeValue, setLocale]             = useSessionStorage<LocaleState>(SessionStorageKeys.Locale);

  const [inviteTemplatesValue, setInviteTemplates] = useSessionStorage<Map<Map<EmailBody>>>(
    SessionStorageKeys.InviteEmailTemplates,
  );

  const [approverTemplatesValue, setApproverTemplates] = useSessionStorage<Map<Map<EmailBody>>>(
    SessionStorageKeys.ApproverEmailTemplates,
  );

  const [raterApprovalTemplatesValue, setRaterApprovalTemplates] = useSessionStorage<Map<Map<EmailBody>>>(
    SessionStorageKeys.RaterEmailTemplates,
  );

  const [eqScoresTemplatesValue, setEqScoresTemplates] = useSessionStorage<Map<EmailBody>>(
    SessionStorageKeys.EQScoresEmailTemplates,
  );

  // Session Storage -> Redux ------------------------------------------------------------------------------------------
  const dispatch = useDispatch();

  useMount(() => {
    dispatch(updateStateAction({
      sessionStorageLoaded: true,
      devPlan:              {
        currentCollaborator: collabValue,
      },
      auth:                 authValue || {},
      config:               configValue,
      featureFlags:         featureFlagsValue,
      locale:               localeValue || defaultLocaleState,
    }));
  });

  // Redux -> Session Storage ------------------------------------------------------------------------------------------
  const auth         = useAppState(state => state.auth);
  const collaborator = useAppState(state => state.devPlan?.currentCollaborator);
  const config       = useAppState(state => state.config);
  const featureFlags = useAppState(state => state.featureFlags);
  const locale       = useAppState(state => state.locale);

  const inviteTemplates        = useAppState(state => state.inviteEmailTemplates);
  const approverTemplates      = useAppState(state => state.approverEmailTemplates);
  const raterApprovalTemplates = useAppState(state => state.raterApprovalEmailTemplates);
  const eqScoresEmailTemplates = useAppState(state => state.eqScoresEmailTemplates);

  React.useEffect(() => auth && setAuth(auth), [auth, setAuth]);
  React.useEffect(() => collaborator && setCollab(collaborator), [collaborator, setCollab]);
  React.useEffect(() => config && setConfig(config), [config, setConfig]);
  React.useEffect(() => featureFlags && setFeatureFlags(featureFlags), [featureFlags, setFeatureFlags]);
  React.useEffect(() => locale && setLocale(locale), [locale, setLocale]);

  // Wrap session storage current value so we're not updating session storage when we see that session storage was
  // updated -- which would be the result of putting the session storage value directly on the dependencies of the
  // session storage update hooks down below.
  const inviteRef        = React.useRef(inviteTemplatesValue);
  const approverRef      = React.useRef(approverTemplatesValue);
  const raterApprovalRef = React.useRef(raterApprovalTemplatesValue);
  const eqScoresRef      = React.useRef(eqScoresTemplatesValue);

  React.useEffect(() => { inviteRef.current = inviteTemplatesValue; }, [inviteTemplatesValue, inviteRef]);
  React.useEffect(() => { approverRef.current = approverTemplatesValue; }, [approverTemplatesValue, approverRef]);
  React.useEffect(
    () => { raterApprovalRef.current = raterApprovalTemplatesValue; },
    [raterApprovalTemplatesValue, raterApprovalRef],
  );
  React.useEffect(
    () => { eqScoresRef.current = eqScoresTemplatesValue; },
    [eqScoresTemplatesValue, eqScoresRef],
  );

  React.useEffect(
    () => inviteTemplates && setInviteTemplates(R.mergeDeepRight(inviteRef.current || {}, inviteTemplates)),
    [inviteTemplates, inviteRef, setInviteTemplates],
  );

  React.useEffect(
    () => approverTemplates && setApproverTemplates(R.mergeDeepRight(approverRef.current || {}, approverTemplates)),
    [approverTemplates, approverRef, setApproverTemplates],
  );

  React.useEffect(
    () => raterApprovalTemplates && setRaterApprovalTemplates(
      R.mergeDeepRight(raterApprovalRef.current || {}, raterApprovalTemplates),
    ),
    [raterApprovalTemplates, raterApprovalRef, setRaterApprovalTemplates],
  );

  React.useEffect(
    () => eqScoresEmailTemplates && setEqScoresTemplates(
      R.mergeDeepRight(eqScoresRef.current || {}, eqScoresEmailTemplates),
    ),
    [eqScoresEmailTemplates, eqScoresRef, setEqScoresTemplates],
  );

  return null; // Render nothing
};

export default AppStateSessionStorageBridge;
